import { useEffect, useState } from "react"
import { NativeBridge } from "@/model/NativeBridge"
import { usePlatform } from "./usePlatform"
import { BREAKPOINTS } from "@shared/constants/breakpoints"

// Safari-specific Navigator extension for PWA detection
interface SafariNavigator extends Navigator {
  standalone?: boolean
}

interface WindowSize {
  width: number | undefined
  height?: number | undefined
}

/**
 * Device detection result containing various device and platform checks
 */
interface DeviceType {
  /** True if app is running as Progressive Web App */
  isPWA: boolean
  /** True if screen width < md breakpoint (768px) */
  isMobile: boolean
  /** True if screen width >= md and < lg breakpoint (768-1023px) */
  isTablet: boolean
  /** True if screen width >= md breakpoint (768px+) */
  isDesktop: boolean
  /** True if screen width >= xl breakpoint (1280px+) */
  isLaptop: boolean
  /** True if screen width < xl breakpoint (used for analysis views) */
  isForAnalysis: boolean
  /** True if device supports touch input */
  isTouchDevice: boolean
  /** True if browser is running on iOS (Safari, Chrome on iOS, etc.) */
  isIOS: boolean
  /** True if running as native iOS app with NativeBridge */
  isIOSApp: boolean
  /** True if running as any native app (iOS or Android) with NativeBridge */
  isNativeApp: boolean
  /** True if running as native Android app with NativeBridge */
  isAndroidApp: boolean

  windowSize: WindowSize
}

/**
 * Browser-based iOS detection using user agent
 * Note: This detects iOS browsers, not the native app
 * For native app detection, use isIOSApp from useDevice hook
 */
export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

// Width thresholds that flip the booleans returned from useDevice.
// `md` flips isMobile/isDesktop, `lg` flips isTablet, `xl` flips
// isLaptop/isForAnalysis. Two widths fall in the same bucket iff they're
// on the same side of every threshold here.
const BREAKPOINT_THRESHOLDS = [BREAKPOINTS.md, BREAKPOINTS.lg, BREAKPOINTS.xl] as const

function sameBucket(a: number, b: number): boolean {
  for (const bp of BREAKPOINT_THRESHOLDS) {
    if (a < bp !== b < bp) return false
  }
  return true
}

function appIsPWA() {
  // Safari iOS specific check for PWA
  if ((navigator as SafariNavigator).standalone) {
    return true
  }

  // Check for Android, Chrome, etc.
  return window.matchMedia("(display-mode: standalone)").matches
}

/**
 * Hook for comprehensive device and platform detection
 *
 * Combines browser-based detection with NativeBridge platform info to provide:
 * - Screen size breakpoints (mobile, desktop, laptop)
 * - Platform detection (iOS browser vs iOS app)
 * - PWA and touch capability detection
 *
 * @returns DeviceType object with all device/platform flags
 *
 * @example
 * const { isIOSApp, isMobile } = useDevice()
 *
 * if (isIOSApp && isMobile) {
 *   // Show iOS app-specific mobile UI
 * }
 */
const useDevice = (): DeviceType => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
  })

  const { isIOS: isIOSNative, isAndroid: isAndroidNative } = usePlatform()

  useEffect(() => {
    const handleResize = () => {
      setWindowSize((prev) => {
        const newWidth = window.innerWidth
        if (prev.width === undefined) return { width: newWidth }
        // Only commit a new state object when the width has crossed a
        // breakpoint that flips one of the derived booleans (isMobile /
        // isTablet / isDesktop / isLaptop / isForAnalysis). Browsers fire
        // `resize` per pixel during a window-edge drag; we have ~60
        // useDevice consumers, all of which read only the discrete flags,
        // so a per-pixel state update was cascading 30+ component
        // re-renders per resize event and making /trainings visibly lag
        // while resizing. The stored `windowSize.width` thus reflects
        // the width sampled at the last bucket crossing — every flag
        // stays correct because both values fall in the same bucket.
        if (sameBucket(prev.width, newWidth)) return prev
        return { width: newWidth }
      })
    }
    window.addEventListener("resize", handleResize)
    handleResize()

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const screenWidth = windowSize.width !== undefined ? windowSize.width : screen.width

  const isMobile = screenWidth < BREAKPOINTS.md
  const isTablet = screenWidth >= BREAKPOINTS.md && screenWidth < BREAKPOINTS.lg
  const isDesktop = screenWidth >= BREAKPOINTS.md
  const isLaptop = screenWidth >= BREAKPOINTS.xl
  const isForAnalysis = screenWidth < BREAKPOINTS.xl
  const isPWA = appIsPWA()
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0
  const isNativeApp = NativeBridge.isAvailable()
  const isIOSApp = isNativeApp && isIOSNative // Now specifically checks for iOS native app
  const isAndroidApp = isNativeApp && isAndroidNative

  return {
    windowSize,
    isPWA,
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    isForAnalysis,
    isLaptop,
    isIOS,
    isIOSApp,
    isNativeApp,
    isAndroidApp,
  }
}

export default useDevice
